import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '@/services/authService';
import AuthSingleCardLayout from '@/components/auth/AuthSingleCardLayout';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import { FiMail, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await authService.requestPasswordReset(email);
      setSubmitted(true);
    } catch (err: any) {
      // Always show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSingleCardLayout
      badge="Reset password"
      title="Recover your access"
      subtitle="Enter your email and we’ll send a secure reset link if the account exists."
    >
          {submitted ? (
            <div className="text-center space-y-4">
              <FiCheckCircle className="mx-auto w-12 h-12 text-done" />
              <h3 className="text-lg font-semibold text-slate-950">Check your email</h3>
              <p className="text-sm text-slate-600">
                If an account exists for <span className="font-medium">{email}</span>, you will
                receive a password reset link shortly.
              </p>
              <Link
                to="/login"
                className="inline-block mt-4 text-sm font-semibold text-brand hover:text-brand-dark"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="flex items-start rounded-2xl border border-red-200 bg-red-50 p-4">
                  <FiAlertCircle className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <Input
                id="email"
                name="email"
                type="email"
                label="Email address"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                leftIcon={<FiMail className="w-5 h-5" />}
                fullWidth
                required
                autoComplete="email"
              />

              <Button type="submit" variant="primary" size="lg" fullWidth isLoading={loading}>
                Send reset link
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm font-semibold text-brand hover:text-brand-dark"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
    </AuthSingleCardLayout>
  );
}
